import orderBy from 'lodash/orderBy';
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';
import { getUserMaterialWithScores, UserMaterial } from '../../../../api/user-material';
import IdentityContext from '../../../../contexts/IdentityContext';
import { useFeatureToggles } from '../../../../contexts/FeatureTogglesContext';
import useThrowAsyncError from '../../../../hooks/useThrowAsyncError';
import ModuleContext from '../../_contexts/ModuleContext';
import TableOfContentsContext from './TableOfContentsContext';

type UserMaterialContextValue = {
  userMaterial: UserMaterial[];
  setUserMaterial: React.Dispatch<React.SetStateAction<UserMaterial[]>>;
  fetchUserMaterial: (updateLoadingState?: boolean) => Promise<void>;
  isLoading: boolean;
};

const UserMaterialContext = createContext<UserMaterialContextValue>({
  userMaterial: [],
  setUserMaterial: () => undefined,
  fetchUserMaterial: () => Promise.resolve(),
  isLoading: true,
});

export default UserMaterialContext;

export function UserMaterialContextProvider({ children }: PropsWithChildren) {
  const handleError = useThrowAsyncError();

  const { identity } = useContext(IdentityContext);
  const { selectedModule } = useContext(ModuleContext);
  const { selectedNode } = useContext(TableOfContentsContext);
  const featureToggles = useFeatureToggles();

  const [userMaterial, setUserMaterial] = useState<UserMaterial[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserMaterial = useCallback(
    async (updateLoadingState = true) => {
      if (!selectedModule || !selectedNode || !identity.isTeacherAlike) return;

      if (updateLoadingState) setIsLoading(true);

      try {
        const { data } = await getUserMaterialWithScores(
          selectedModule.id,
          selectedNode.id,
          Boolean(featureToggles.BUNDLES),
        );

        setUserMaterial(orderBy(data, ['lastUpdatedAt'], ['desc']));
      } catch (e) {
        handleError(e);
      } finally {
        if (updateLoadingState) setIsLoading(false);
      }
    },
    [featureToggles.BUNDLES, handleError, identity.isTeacherAlike, selectedModule, selectedNode],
  );

  useEffect(() => {
    if (identity.isTeacherAlike) fetchUserMaterial();
    else setIsLoading(false);
  }, [fetchUserMaterial, identity.isTeacherAlike]);

  return (
    <UserMaterialContext.Provider
      value={{
        userMaterial,
        setUserMaterial,
        fetchUserMaterial,
        isLoading,
      }}
    >
      {children}
    </UserMaterialContext.Provider>
  );
}
